import React from 'react';
import { Square, Monitor, Smartphone, RectangleVertical } from 'lucide-react';
import { AspectRatio } from './template.schemas';

/**
 * Configuration interface for aspect ratio options
 */
export interface AspectRatioConfig {
  /** The aspect ratio value */
  value: AspectRatio;
  /** Display label in German */
  label: string;
  /** Icon component */
  icon: React.ReactNode;
  /** Description with platform examples */
  description: string;
}

/**
 * Centralized aspect ratio configuration
 * Used across the application for consistent aspect ratio handling
 */
export const ASPECT_RATIO_CONFIG: AspectRatioConfig[] = [
  { 
    value: "1:1", 
    label: "Quadrat", 
    icon: <Square className="h-4 w-4" />,
    description: "1:1 • Instagram Post"
  },
  { 
    value: "4:5", 
    label: "Portrait", 
    icon: <RectangleVertical className="h-4 w-4" />,
    description: "4:5 • Instagram Portrait"
  },
  { 
    value: "16:9", 
    label: "Querformat", 
    icon: <Monitor className="h-4 w-4" />,
    description: "16:9 • YouTube, LinkedIn"
  },
  { 
    value: "9:16", 
    label: "Hochformat", 
    icon: <Smartphone className="h-4 w-4" />,
    description: "9:16 • Instagram Story, TikTok"
  },
];

/**
 * Get aspect ratio configuration by value
 * @param aspectRatio - The aspect ratio to find
 * @returns The configuration object or undefined if not found
 */
export const getAspectRatioConfig = (aspectRatio: AspectRatio): AspectRatioConfig | undefined => {
  return ASPECT_RATIO_CONFIG.find(config => config.value === aspectRatio);
};

/**
 * Get display label for aspect ratio
 * @param aspectRatio - The aspect ratio value
 * @returns The display label or the value itself if not found
 */
export const getAspectRatioLabel = (aspectRatio: AspectRatio): string => {
  const config = getAspectRatioConfig(aspectRatio);
  return config?.label || aspectRatio;
};